/**
 * The control plane's two write gates, as every request meets them — in ONE
 * place, called by lib/api.ts, rather than by each surface (GOV-09, GOV-01).
 *
 * # A freeze (423) and the recorded override
 *
 * A write a change window freezes is answered 423, naming the window, its
 * author and its end. A holder of governance.write may send that one write
 * through with a reason, which the server records on the window and on the
 * operator trail BEFORE the write is made (X-Change-Window-Override). Each
 * surface used to show the 423 as an error and stop, so the override existed
 * only for people who could set a header by hand. Here, when the account holds
 * governance.write and the server offers the override, the operator is asked
 * for the reason; the same request is sent again carrying it, once. Declining
 * leaves the refusal exactly as the server wrote it.
 *
 * # A parked write (202)
 *
 * With change control on, the dangerous direction of a policy write — a ladder
 * lower than the tenant's, a VPN policy that widens access, a detection
 * removal, a suppression, a protect-list removal, switching change control off
 * — is PARKED for a second operator and answered 202: nothing has changed. The
 * surfaces that make those writes were written before any of them could be
 * parked, and read any 2xx as done, so "Published" would be shown for a
 * publish that is waiting in a queue. A parked request is therefore surfaced
 * as an ApiError carrying the server's own sentence. Only these new parked
 * writes (`approval.scope === "request"`): containment, fleet arming and the
 * assistant loosening were parked before, and the surfaces that make them
 * read `approval_required` themselves.
 */
import { capabilitiesNow } from "./capabilities";

export const CHANGE_WINDOW_OVERRIDE_HEADER = "X-Change-Window-Override";

/** The fields of a 423 the override offer reads. */
export interface FrozenRefusal {
  /** The server's sentence: what is frozen, until when, by whom and why. */
  error: string;
  /** The kind of change frozen. */
  kind?: string;
  /** What the server says the override does. */
  override: string;
}

type Prompt = (refusal: FrozenRefusal) => Promise<string | null> | string | null;

/**
 * How the operator is asked for an override's reason: the kit's OverrideDialog
 * (forms plan F36, X6), which shows the 423's own sentence, says the reason is
 * recorded on the window and the operator trail before the write, and waits on
 * a reason of the server's minimum with its count — never the browser's
 * prompt, which dropped a short reason silently. Loaded when first needed, so
 * this module stays free of the component layer. A dialog that cannot be
 * shown is a decline.
 */
export const overrideDialogPrompt: Prompt = (refusal) => {
  if (typeof document === "undefined") return null;
  return import("../features/common/form/OverrideDialog")
    .then((m) => m.openOverrideDialog(refusal))
    .catch(() => null);
};

let prompt: Prompt = overrideDialogPrompt;

export function setChangeWindowOverridePrompt(next: Prompt | null): void {
  prompt =
    next ??
    (() => {
      return null;
    });
}

/** The server's rule for a reason: at least three characters. */
const MIN_REASON = 3;

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

function isUnsafe(method: string): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
}

/**
 * The override this answer offers THIS account, or null: a 423 freeze that
 * names an override, for a write, not already refused an override, to an
 * account whose last whoami listed governance.write. An account whose
 * capabilities are not known is not offered one — the server would refuse it,
 * and asking for a reason it then refuses wastes the operator's words.
 */
export function overrideOffered(status: number, method: string, body: unknown): FrozenRefusal | null {
  if (status !== 423 || !isUnsafe(method) || !isRecord(body)) return null;
  if (body.status !== "CHANGE_WINDOW_FROZEN" || typeof body.override !== "string" || body.override_refused) return null;
  const caps = capabilitiesNow();
  if (!caps || !caps.has("governance.write")) return null;
  return {
    error: typeof body.error === "string" ? body.error : "this change is frozen",
    kind: typeof body.kind === "string" ? body.kind : undefined,
    override: body.override
  };
}

/**
 * ONE ASK PER OPERATION. A bulk run (form/bulk.ts runEach) makes one request
 * per item and each request meets the freeze on its own, so an operator who
 * sent item k through would be asked again for item k+1 — "never asks an
 * override N times" (forms plan §2.2). Inside `withOneOverrideAsk(fn)` the
 * prompt is asked at most once; any later freeze inside it is a decline
 * without asking, so that write stays refused (423) and the run stops there.
 * The scope ends when the outermost call settles.
 */
let askScopeDepth = 0;
let askedInScope = false;

export async function withOneOverrideAsk<T>(fn: () => Promise<T>): Promise<T> {
  askScopeDepth += 1;
  try {
    return await fn();
  } finally {
    askScopeDepth -= 1;
    if (askScopeDepth === 0) askedInScope = false;
  }
}

/** Ask for the reason; null when declined or too short to be recorded. */
export async function askForOverride(refusal: FrozenRefusal): Promise<string | null> {
  if (askScopeDepth > 0) {
    if (askedInScope) return null;
    askedInScope = true;
  }
  const answer = await prompt(refusal);
  const reason = typeof answer === "string" ? answer.trim() : "";
  return reason.length >= MIN_REASON ? reason : null;
}

/** The header's value: percent-encoded, which the server decodes, so any script survives. */
export function encodeOverrideReason(reason: string): string {
  return encodeURIComponent(reason);
}

/**
 * The refusal a parked policy write reads as, or null. The server's detail
 * says what is held, why, and until when; the surface shows it as it would
 * any refusal, because nothing was changed.
 */
export function parkedRefusal(status: number, body: unknown): string | null {
  if (status !== 202 || !isRecord(body) || body.approval_required !== true) return null;
  const approval = isRecord(body.approval) ? body.approval : null;
  if (!approval || approval.scope !== "request") return null;
  const detail = typeof body.detail === "string" && body.detail ? body.detail : "this change needs a second operator";
  return `Held for a second operator — nothing has changed yet. ${detail}`;
}

/** A write parked for a second operator, as the 202's own fields (forms plan §2.2, GateCard "held"). */
export interface HeldGate {
  kind: "held";
  approvalId: string;
  /** The server's sentence: what is held, why, until when. */
  detail: string;
  requester?: string;
  createdAt?: string;
  expiresAt?: string;
  /** The change's direction (policychange): "widens-access". */
  direction?: string;
  directionWhy?: string;
  /** The hash of exactly the request that approving replays. */
  bodySha256?: string;
  summary?: string;
  label?: string;
}

/**
 * The structured sibling of parkedRefusal(): the held write's own fields, so a
 * surface can draw the held GateCard (request, hash, until when, Open in
 * Approvals) instead of one flattened string. Null for anything else. Held is
 * neither a success nor an error: nothing has changed yet.
 */
export function heldOf(status: number, body: unknown): HeldGate | null {
  if (status !== 202 || !isRecord(body) || body.approval_required !== true) return null;
  const approval = isRecord(body.approval) ? body.approval : null;
  if (!approval || approval.scope !== "request") return null;
  const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
  const id = str(approval.id);
  if (!id) return null;
  return {
    kind: "held",
    approvalId: id,
    detail: str(body.detail) ?? "this change needs a second operator",
    requester: str(approval.requester),
    createdAt: str(approval.created_at),
    expiresAt: str(approval.expires_at),
    direction: str(approval.direction),
    directionWhy: str(approval.direction_why),
    bodySha256: str(approval.body_sha256),
    summary: str(approval.summary) ?? str(body.summary),
    label: str(body.label)
  };
}
